import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { MessageCircle, UserCheck, CheckCircle2, Plus, Send, ArrowLeft, X, Paperclip, LifeBuoy, Inbox, Clock, Search, Filter, MoreHorizontal, Check, Zap, HeartHandshake, Eye, Award, Calendar, User, Tag, Flag, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, Badge, Pill, BtnPrimary, Sparkline } from "./ui";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const CATEGORIES = [
  ["webseite", "Website"], ["inhalt", "Inhalt / Texte"], ["bilder", "Bilder"], ["technik", "Technik"],
  ["domain", "Domain / DNS"], ["email", "E-Mail"], ["rechnung", "Rechnung"], ["login", "Login / Zugang"],
  ["cockpit", "Kunden-Cockpit"], ["modul", "Modul & Booster"], ["allgemein", "Allgemeine Frage"], ["sonstiges", "Sonstiges"],
];
const STATUS_TONE = { neu: "slate", in_bearbeitung: "blue", warten_auf_kunde: "amber", warten_auf_kewag: "blue", loesung_vorgeschlagen: "amber", erledigt: "green", archiviert: "slate" };
const FILTERS = [["alle", "Alle"], ["offen", "Offen"], ["aktion", "Ihre Aktion"], ["erledigt", "Erledigt"]];
const PRIO_FILTERS = [["alle", "Alle Prioritäten"], ["hoch", "Hoch"], ["mittel", "Mittel"], ["niedrig", "Niedrig"]];
const WAVES = { blue: [4, 7, 5, 9, 7, 11, 9, 13, 11, 15], amber: [3, 4, 6, 5, 8, 6, 9, 8, 11, 13], green: [5, 6, 5, 7, 6, 8, 7, 10, 9, 12] };
const VALUES = [
  { icon: Zap, title: "Schnelle Hilfe", text: "Wir kümmern uns zügig um Ihr Anliegen." },
  { icon: HeartHandshake, title: "Persönlicher Support", text: "Ihr KEWAG-Team kennt Ihr Projekt." },
  { icon: Eye, title: "Transparenter Prozess", text: "Sie sehen jederzeit den Stand Ihres Falls." },
  { icon: Award, title: "Beste Lösung", text: "Wir bleiben dran, bis alles passt." },
];
const LOCALES = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };
function relTime(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso), diff = (d.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(LOCALES[lang] || "de-CH", { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 2592000) return rtf.format(Math.round(diff / 86400), "day");
  return d.toLocaleDateString(LOCALES[lang] || "de-CH", { day: "2-digit", month: "short" });
}
const fmtDT = (iso) => { try { const d = new Date(iso), p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`; } catch { return ""; } };
const initials = (n) => (n || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const stagePct = (s) => ({ neu: 15, in_bearbeitung: 45, warten_auf_kewag: 40, warten_auf_kunde: 60, loesung_vorgeschlagen: 85, erledigt: 100, archiviert: 100 }[s] ?? 30);

export default function CSupport() {
  const { accent, clientId, openAi } = useCockpit();
  const { t, lang } = useI18n();
  const location = useLocation();
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [prio, setPrio] = useState("alle");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const qs = clientId ? `?client_id=${clientId}` : "";

  const load = () => api.get(`/cockpit/support${qs}`).then((r) => setTickets(r.data));
  const loadDetail = (id) => api.get(`/cockpit/support/${id}${qs}`).then((r) => setDetail(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);
  useEffect(() => { if (new URLSearchParams(location.search).get("compose") === "1") setShowNew(true); }, [location.search]);
  useEffect(() => { if (sel) loadDetail(sel); else setDetail(null); /* eslint-disable-next-line */ }, [sel]);

  const isDone = (tk) => ["erledigt", "archiviert"].includes(tk.status);
  useEffect(() => {
    if (!tickets || sel) return;
    const desktop = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
    if (desktop && tickets.length) setSel([...tickets].sort((a, b) => new Date(b.last_at || b.created_at) - new Date(a.last_at || a.created_at))[0].id);
  }, [tickets]); // eslint-disable-line
  if (!tickets) return <Spinner />;

  const open = tickets.filter((tk) => !isDone(tk));
  const done = tickets.filter(isDone);
  const actions = tickets.filter((tk) => !isDone(tk) && (tk.needs_reply || tk.can_confirm));

  let shown = tickets.filter((tk) => {
    if (filter === "offen") return !isDone(tk);
    if (filter === "aktion") return !isDone(tk) && (tk.needs_reply || tk.can_confirm);
    if (filter === "erledigt") return isDone(tk);
    return true;
  });
  if (prio !== "alle") shown = shown.filter((tk) => (tk.priority || "") === prio);
  if (q.trim()) { const qq = q.toLowerCase(); shown = shown.filter((tk) => (tk.subject || "").toLowerCase().includes(qq) || (tk.messages || []).some((m) => (m.text || "").toLowerCase().includes(qq))); }
  shown = shown.sort((a, b) => new Date(b.last_at || b.created_at) - new Date(a.last_at || a.created_at));

  return (
    <div className="fade-in space-y-5">
      {/* Kopf */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}><LifeBuoy className="w-4 h-4" /> {t("Support")}</p>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight mt-1">{t("Support-Center")}</h1>
          <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("Ihre Supportfälle bei KEWAG — alles an einem Ort.")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="csupport-search" placeholder={t("Supportfälle durchsuchen…")} className="pl-9 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-500 outline-none focus:border-white/30 transition-colors w-full sm:w-60" />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="csupport-filter-btn" className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold text-gray-200 hover:bg-white/10 transition-colors flex items-center gap-2"><Filter className="w-4 h-4" /> {t("Filter")}{prio !== "alle" && <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />}</button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 bg-[#16161c] border-white/10 text-gray-300 p-1.5" data-testid="csupport-filter-menu">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 px-2 py-1.5">{t("Nach Priorität")}</div>
              {PRIO_FILTERS.map(([k, l]) => (
                <button key={k} onClick={() => setPrio(k)} data-testid={`csupport-prio-${k}`} className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm hover:bg-white/5 transition-colors"><span className={prio === k ? "font-semibold text-white" : ""}>{t(l)}</span>{prio === k && <Check className="w-4 h-4" style={{ color: accent }} />}</button>
              ))}
            </PopoverContent>
          </Popover>
          <BtnPrimary accent={accent} onClick={() => openAi()} data-testid="csupport-new-btn" className="shrink-0 !py-2.5"><MessageCircle className="w-4 h-4" /> {t("Frage stellen")}</BtnPrimary>
        </div>
      </div>

      {/* KPI mit Wellen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SupportStat tone="blue" icon={MessageCircle} value={open.length} label="Offene Fälle" sub="in Bearbeitung" accent={accent} t={t} testid="csupport-open" />
        <SupportStat tone={actions.length ? "amber" : "green"} icon={UserCheck} value={actions.length} label="Ihre Aktion" sub={actions.length ? "warten auf Sie" : "nichts zu tun"} active={actions.length > 0} accent={accent} t={t} testid="csupport-actions" />
        <SupportStat tone="green" icon={CheckCircle2} value={done.length} label="Erledigt" sub="abgeschlossen" accent={accent} t={t} testid="csupport-done" />
      </div>

      {/* Filter-Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="csupport-tabs">
        {FILTERS.map(([k, l]) => <Pill key={k} active={filter === k} accent={accent} testid={`csupport-filter-${k}`} onClick={() => setFilter(k)}>{t(l)}</Pill>)}
      </div>

      {/* Zwei-Spalten */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-5 items-start">
        <div className={`space-y-3 ${sel ? "hidden lg:block" : "block"}`} data-testid="csupport-queue">
          {shown.length === 0 ? (
            <Card className="p-8 flex flex-col items-center text-center"><Inbox className="w-8 h-8 text-gray-700 mb-2" /><p className="text-sm text-gray-300 font-semibold">{t("Keine Supportfälle in dieser Ansicht.")}</p><p className="text-xs text-gray-500 mt-1 max-w-[280px] leading-relaxed">{t("Oben eine Frage stellen oder ein Problem melden — KEWAG antwortet hier.")}</p></Card>
          ) : shown.map((tk) => {
            const active = sel === tk.id;
            const flag = tk.needs_reply || tk.can_confirm;
            const dot = flag ? "#fbbf24" : isDone(tk) ? accent : "#3B82F6";
            return (
              <button key={tk.id} onClick={() => setSel(tk.id)} data-testid={`csupport-ticket-${tk.id}`}
                className={`w-full text-left rounded-xl p-4 border transition-colors relative overflow-hidden ${active ? "" : "border-white/5 bg-[#15181e] hover:border-white/15"}`}
                style={active ? { background: `${accent}12`, borderColor: `${accent}66`, boxShadow: `inset 0 0 0 1px ${accent}33` } : {}}>
                {active && <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5" style={{ background: dot }} />
                    <div className="min-w-0"><div className="font-semibold text-sm leading-snug line-clamp-1">{tk.subject}</div><div className="text-xs text-gray-500 mt-0.5 truncate">{t(tk.category_label)} · {tk.ticket_no}</div></div>
                  </div>
                  <Badge tone={STATUS_TONE[tk.status] || "slate"} accent={accent}>{t(tk.status_label)}</Badge>
                </div>
                <div className="flex items-center justify-between mt-2.5 text-[11px] text-gray-500">
                  <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {tk.message_count}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {relTime(tk.last_at, lang)}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className={`${sel ? "block" : "hidden lg:block"}`}>
          {detail ? (
            <TicketDetail ticket={detail} accent={accent} qs={qs} lang={lang} t={t} onBack={() => setSel(null)} onReload={() => { loadDetail(detail.id); load(); }} />
          ) : (
            <Card className="hidden lg:flex flex-col items-center justify-center text-center py-20 px-6" data-testid="csupport-empty">
              <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${accent}1A`, color: accent }}><LifeBuoy className="w-7 h-7" /></span>
              <p className="font-semibold">{t("Wählen Sie einen Supportfall")}</p>
              <p className="text-sm text-gray-500 mt-1 max-w-sm">{t("Wählen Sie links einen Fall aus, um die Konversation zu sehen — oder eröffnen Sie einen neuen Fall.")}</p>
            </Card>
          )}
        </div>
      </div>

      {/* Werte-Zeile */}
      <Card className="p-5 lg:p-6" data-testid="csupport-values">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {VALUES.map((v) => (
            <div key={v.title} className="flex flex-col gap-2">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}><v.icon className="w-5 h-5" /></span>
              <div className="font-bold text-[15px]">{t(v.title)}</div><div className="text-sm text-gray-400 leading-relaxed">{t(v.text)}</div>
            </div>
          ))}
        </div>
      </Card>

      {showNew && <NewTicket accent={accent} qs={qs} t={t} onClose={() => setShowNew(false)} onCreated={(tk) => { setShowNew(false); load(); setSel(tk.id); }} />}
    </div>
  );
}

const SupportStat = ({ tone, icon: Icon, value, label, sub, active, accent, t, testid }) => {
  const c = tone === "blue" ? "#3B82F6" : tone === "amber" ? "#fbbf24" : accent;
  return (
    <div data-testid={testid} className="relative overflow-hidden rounded-2xl border p-5 lg:p-6 min-h-[150px] flex flex-col justify-between bg-[#101015]" style={{ borderColor: active ? `${c}55` : "rgba(255,255,255,0.06)" }}>
      <div className="absolute inset-x-0 bottom-0 h-20 opacity-40 pointer-events-none"><Sparkline data={WAVES[tone] || WAVES.green} color={c} height={80} /></div>
      <div className="relative flex items-start gap-4">
        <span className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${c}1F`, color: c }}><Icon className="w-7 h-7" /></span>
        <div className="text-5xl font-black tracking-tight leading-none pt-1">{value}</div>
      </div>
      <div className="relative mt-2"><div className="font-bold text-lg leading-tight">{t(label)}</div><div className="text-sm text-gray-500">{t(sub)}</div></div>
    </div>
  );
};

function TicketDetail({ ticket, accent, qs, lang, t, onBack, onReload }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const replyRef = useRef(null); const fileRef = useRef(null);
  const done = ["erledigt", "archiviert"].includes(ticket.status);
  const pct = stagePct(ticket.status);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.post(`/cockpit/support/${ticket.id}/reply${qs}`, { text: reply }); setReply(""); toast.success(t("Nachricht gesendet")); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Konnte nicht senden.")); }
    finally { setBusy(false); }
  };
  const closeCase = async () => {
    setBusy(true);
    try { await api.post(`/cockpit/support/${ticket.id}/confirm${qs}`); toast.success(t("Supportfall geschlossen — danke!")); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Konnte nicht schliessen.")); }
    finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/cockpit/support/${ticket.id}/reply${qs}`, { text: t("Datei angehängt:") + " " + file.name, attachment_url: data.url, attachment_label: file.name });
      toast.success(t("Datei hochgeladen")); onReload();
    } catch (er) { toast.error(er.response?.data?.detail || t("Upload fehlgeschlagen")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const focusReply = () => { replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => replyRef.current?.focus(), 300); };

  const metas = [
    { icon: Calendar, label: "Erstellt am", value: fmtDT(ticket.created_at), c: accent },
    { icon: User, label: "Kunde", value: ticket.company || "—", c: "#3B82F6" },
    { icon: Tag, label: "Kategorie", value: t(ticket.category_label), c: "#8B5CF6" },
    { icon: Flag, label: "Priorität", value: ticket.priority_label, c: "#fbbf24", badge: true },
  ];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-4 items-start" data-testid="csupport-detail">
      <div className="space-y-4">
        <button onClick={onBack} className="lg:hidden inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white" data-testid="csupport-back"><ArrowLeft className="w-4 h-4" /> {t("Zurück zur Übersicht")}</button>
        {/* Kopf */}
        <Card className="p-5">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0"><h2 className="text-xl font-black tracking-tight leading-snug">{ticket.subject}</h2><div className="text-xs text-gray-500 mt-1">{t(ticket.category_label)} · {t("Ticket")} {ticket.ticket_no}</div></div>
            <div className="flex items-center gap-2">
              <Badge tone={STATUS_TONE[ticket.status] || "slate"} accent={accent}>{t(ticket.status_label)}</Badge>
              <Popover>
                <PopoverTrigger asChild><button data-testid="csupport-menu" className="text-gray-500 hover:text-white transition-colors"><MoreHorizontal className="w-5 h-5" /></button></PopoverTrigger>
                <PopoverContent align="end" className="w-44 bg-[#16161c] border-white/10 text-gray-300 p-1.5">
                  <button onClick={focusReply} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-white/5"><Send className="w-4 h-4" /> {t("Nachricht senden")}</button>
                  {!done && <button onClick={closeCase} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-white/5"><CheckCircle2 className="w-4 h-4" /> {t("Fall schliessen")}</button>}
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {ticket.paket_hinweis && <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border" style={{ borderColor: `${accent}55`, color: accent }} data-testid="csupport-paket-hinweis">{t(ticket.paket_hinweis)}{ticket.customer_benefit ? ` · ${ticket.customer_benefit}` : ""}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/5" data-testid="csupport-meta">
            {metas.map((m) => (
              <div key={m.label}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-1"><m.icon className="w-3 h-3" style={{ color: m.c }} /> {t(m.label)}</div>
                {m.badge ? <Badge tone="amber" accent={accent}>{m.value}</Badge> : <div className="text-sm font-semibold text-gray-200 truncate">{m.value}</div>}
              </div>
            ))}
          </div>
        </Card>

        {/* Konversation */}
        <Card className="p-5">
          <div className="space-y-4 max-h-[52vh] overflow-y-auto pr-1" data-testid="csupport-thread">
            {(ticket.messages || []).map((m, i) => {
              const kewag = m.sender === "kewag";
              return (
                <div key={i} className={`flex items-end gap-2.5 ${kewag ? "flex-row-reverse" : ""}`}>
                  <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold" style={kewag ? { background: accent, color: "#000" } : { background: "rgba(255,255,255,0.08)", color: "#e5e7eb" }}>{initials(m.sender_name)}</span>
                  <div className={`max-w-[80%] ${kewag ? "items-end" : "items-start"} flex flex-col`}>
                    <div className="text-[11px] text-gray-500 mb-1 px-1">{m.sender_name} · {relTime(m.created_at, lang)}</div>
                    <div className={`rounded-2xl px-4 py-2.5 text-sm ${kewag ? "text-black" : "bg-white/5 text-gray-200"}`} style={kewag ? { background: accent } : {}}>
                      <div className="whitespace-pre-wrap">{m.text}</div>
                      {m.attachment_url && <a href={m.attachment_url} target="_blank" rel="noreferrer" className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold underline ${kewag ? "text-black/80" : ""}`} style={kewag ? {} : { color: accent }}><Paperclip className="w-3 h-3" /> {m.attachment_label || t("Anhang öffnen")}</a>}
                    </div>
                    <div className="text-[10px] text-gray-600 mt-1 px-1">{fmtDT(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {!done ? (
            <div className="mt-4 border-t border-white/10 pt-4">
              <div className="flex items-end gap-2">
                <textarea ref={replyRef} value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="csupport-reply-input" rows={2} placeholder={t("Ihre Nachricht an KEWAG…")} className="flex-1 bg-black border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-white/30 resize-none" />
                <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="csupport-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="csupport-attach" title={t("Datei anhängen")} className="shrink-0 w-11 h-11 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 flex items-center justify-center disabled:opacity-40"><Paperclip className="w-4 h-4" /></button>
                <button onClick={send} disabled={busy || !reply.trim()} data-testid="csupport-reply-send" className="shrink-0 w-11 h-11 rounded-xl text-black font-bold disabled:opacity-40 flex items-center justify-center" style={{ background: accent }}><Send className="w-4 h-4" /></button>
              </div>
            </div>
          ) : <div className="mt-4 border-t border-white/10 pt-4 text-sm text-gray-400 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{ color: accent }} /> {t("Dieser Supportfall ist abgeschlossen.")}</div>}
        </Card>
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        <Card className="p-4" data-testid="csupport-side-status">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{t("Status")}</div>
          <div className="flex items-center gap-2 text-sm font-semibold"><span className="w-2 h-2 rounded-full" style={{ background: done ? accent : "#3B82F6" }} /> {t(ticket.status_label)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{t("Fortschritt")}</div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} /></div>
          <div className="text-xs text-gray-400 mt-2">{ticket.paket_hinweis ? t(ticket.paket_hinweis) : t(ticket.status_label)}</div>
        </Card>
        <Card className="p-4" data-testid="csupport-side-assignee">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{t("Zugewiesen an")}</div>
          <div className="flex items-center gap-2.5"><span className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: `${accent}22`, color: accent }}>{initials(ticket.assignee)}</span><div><div className="text-sm font-semibold">{ticket.assignee}</div><div className="text-[11px] text-gray-500">{t("Support-Team")}</div></div></div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2.5">{t("Aktionen")}</div>
          <div className="space-y-2">
            <button onClick={focusReply} data-testid="csupport-action-message" className="w-full text-black font-bold text-sm rounded-lg py-2 flex items-center justify-center gap-1.5" style={{ background: accent }}><Send className="w-4 h-4" /> {t("Nachricht senden")}</button>
            <button onClick={() => fileRef.current?.click()} disabled={uploading || done} data-testid="csupport-action-upload" className="w-full text-sm font-semibold rounded-lg py-2 bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center gap-1.5 disabled:opacity-40"><Paperclip className="w-4 h-4" /> {uploading ? t("Wird hochgeladen…") : t("Datei hochladen")}</button>
            {!done && <button onClick={closeCase} disabled={busy} data-testid="csupport-action-close" className="w-full text-sm font-semibold rounded-lg py-2 bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center gap-1.5 disabled:opacity-40"><CheckCircle2 className="w-4 h-4" /> {t("Fall schliessen")}</button>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function NewTicket({ accent, qs, t, onClose, onCreated }) {
  const [form, setForm] = useState({ subject: "", message: "", category: "allgemein" });
  const [busy, setBusy] = useState(false);
  const errs = (!form.subject.trim() ? 1 : 0) + (!form.message.trim() ? 1 : 0);
  const save = async () => {
    if (errs) { toast.error(t("Bitte Betreff und Nachricht ausfüllen.")); return; }
    setBusy(true);
    try { const { data } = await api.post(`/cockpit/support${qs}`, form); toast.success(t("Supportfall gesendet")); onCreated(data); }
    catch (e) { toast.error(e.response?.data?.detail || t("Konnte nicht senden.")); setBusy(false); }
  };
  const inp = "w-full bg-black border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-white/30";
  return (
    <div className="fixed inset-0 z-[10001] flex items-start justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" data-testid="csupport-new-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg bg-[#121217] border border-white/10 rounded-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-5"><div className="flex items-center gap-2"><LifeBuoy className="w-5 h-5" style={{ color: accent }} /><h3 className="text-lg font-bold tracking-tight">{t("Neuer Supportfall")}</h3></div><button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"><X className="w-5 h-5" /></button></div>
        <div className="space-y-3">
          <div><label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t("Thema")}</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="csupport-new-category" className={`${inp} mt-1`}>{CATEGORIES.map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}</select></div>
          <div><label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t("Betreff")}</label><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="csupport-new-subject" className={`${inp} mt-1`} placeholder={t("Worum geht es?")} /></div>
          <div><label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t("Ihre Nachricht")}</label><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="csupport-new-message" rows={4} className={`${inp} mt-1`} placeholder={t("Beschreiben Sie Ihr Anliegen...")} /></div>
        </div>
        <div className="flex justify-end gap-3 mt-6"><button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button><button onClick={save} disabled={busy || errs > 0} data-testid="csupport-new-save" className="text-black font-bold py-2 px-5 rounded-lg disabled:opacity-40" style={{ background: accent }}>{busy ? "…" : t("Supportfall senden")}</button></div>
      </div>
    </div>
  );
}
